import axios from "axios";
import { api_url } from "../config/config";
import quotationAdapters from "../adapters/quotationAdapters";

export const quotationService = {
  getAllQuotations: () => {
    return axios.get(`${api_url}quotations/search?paginate=0`);
  },
  getValidQuotations: (search) => {
    return axios.get(`${api_url}quotations/valid${search}`);
  },
  getQuotations: (search) => {
    return axios.get(`${api_url}quotations/search${search}`);
  },
  getClientQuotations: (clientUuid) => {
    return axios.get(
      `${api_url}quotations/search?paginate=0&client=${clientUuid}`
    );
  },
  getQuotation: (uuid) => {
    return axios.get(`${api_url}quotations/${uuid}`);
  },
  createQuotation: (data) => {
    return axios.post(
      `${api_url}quotations`,
      quotationAdapters.transformForAPI(data)
    );
  },
  updateQuotation: (data) => {
    return axios.put(
      `${api_url}quotations/${data.uuid}`,
      quotationAdapters.transformForAPI(data)
    );
  },
  updateQuotationStatus: (quotationUuid, rlId, status, datas) => {
    return axios.patch(
      `${api_url}quotations/${quotationUuid}/quotation/${rlId}/update-status`,
      { ...datas, status }
    );
  },
  getConditionsParticulieres: (dossierUuid, detailUuid) => {
    // 404 (corps vide) = aucune CP enregistrée pour cette ligne ; accepté sans erreur.
    return axios.get(
      `${api_url}quotations/${dossierUuid}/details/${detailUuid}/conditions-particulieres`,
      { validateStatus: (status) => status === 200 || status === 404 }
    );
  },
  generateConditionsParticulieres: (dossierUuid, detailUuid, includeCgv = false) => {
    // LS-702 : include_cgv fusionne les CGV du client dans le document persisté
    // côté API (collection dédiée à l'envoi en signature).
    return axios.post(
      `${api_url}quotations/${dossierUuid}/details/${detailUuid}/conditions-particulieres`,
      { include_cgv: includeCgv }
    );
  },
  downloadConditionsParticulieres: (dossierUuid, detailUuid, variant) => {
    // LS-700 : passe par l'API (plutôt que l'URL de stockage brute renvoyée par
    // getConditionsParticulieres) pour rester dans le CORS de l'API en fetch cross-origin.
    // LS-702 (amélioration) : `variant=signe` sert le document signé récupéré depuis Youtrust.
    return axios.get(
      `${api_url}quotations/${dossierUuid}/details/${detailUuid}/conditions-particulieres/download`,
      {
        responseType: "arraybuffer",
        params: variant ? { variant } : undefined,
      }
    );
  },
  previewConditionsParticulieres: (dossierUuid, detailUuid, includeCgv = false) => {
    // LS-702 (amélioration) : rendu à la volée de la CP (fusionnée aux CGV côté serveur si
    // `include_cgv`), sans rien persister — alimente le bouton « Prévisualiser la CP » quand
    // aucun document n'a encore été généré pour la ligne.
    return axios.get(
      `${api_url}quotations/${dossierUuid}/details/${detailUuid}/conditions-particulieres/preview`,
      {
        responseType: "arraybuffer",
        params: includeCgv ? { include_cgv: 1 } : undefined,
      }
    );
  },
  envoyerEnSignature: (dossierUuid, detailUuid, signataire) => {
    // LS-702 : envoie le document déjà généré/persisté (jamais de nouvelle
    // génération à l'envoi). 422 si aucun document n'a été généré pour la ligne,
    // ou si la ligne a déjà été envoyée en signature (pas de renvoi).
    return axios.post(
      `${api_url}quotations/${dossierUuid}/details/${detailUuid}/signature`,
      signataire
    );
  },
  actualiserSignature: (dossierUuid, detailUuid) => {
    // LS-702 addendum : interroge Youtrust (plutôt que d'attendre le webhook) et
    // récupère le document signé s'il est disponible. Retourne le statut à jour.
    return axios.post(
      `${api_url}quotations/${dossierUuid}/details/${detailUuid}/signature/actualiser`
    );
  },
  marquerEnvoiManuel: (dossierUuid, detailUuid) => {
    // LS-692 : canal manuel — le commercial gère l'envoi de la signature hors de
    // l'outil. 422 si aucune CP n'a été générée, ou si un envoi (électronique ou
    // manuel) a déjà eu lieu. Retourne le statut à jour (dont signatureMode).
    return axios.post(
      `${api_url}quotations/${dossierUuid}/details/${detailUuid}/signature/manuelle`
    );
  },
  annulerEnvoiManuel: (dossierUuid, detailUuid) => {
    // LS-692 : remet la ligne à l'état initial. 422 si la ligne n'est pas en
    // canal manuel ou si un document signé a déjà été téléversé.
    return axios.delete(
      `${api_url}quotations/${dossierUuid}/details/${detailUuid}/signature/manuelle`
    );
  },
  televerserDocumentSigneManuel: (dossierUuid, detailUuid, file) => {
    // LS-692 : upload du PDF signé récupéré par le commercial. Disponible
    // uniquement sur une ligne « Proposition envoyée » en canal manuel (422 sinon).
    const formData = new FormData();
    formData.append("document", file);
    return axios.post(
      `${api_url}quotations/${dossierUuid}/details/${detailUuid}/signature/manuelle/document-signe`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
  },
};
